import { getElementsByBreakdownId } from '../../api/element';
import { ElementInfo, ElementWithUserNames } from '../../types/element.types';

// 整个分场的元素缓存（用于重名检查，作为 BreakdownDataCacheContext 的备用）
// 使用模块级 Map 存储，避免触发重新渲染
const elementsCache = new Map<string, {
  items: ElementWithUserNames[];
  timestamp: number; // 缓存时间戳（毫秒）
}>();

// 缓存过期时间：5分钟
const CACHE_EXPIRY_TIME = 5 * 60 * 1000;

/**
 * 获取整个分场的元素列表（优先使用 BreakdownDataCacheContext，如果没有则使用本地缓存或 API）
 * 与 Scene.tsx 中的 getAllElementsInBreakdown 功能相同，但作为独立工具函数
 * 
 * @param breakdownID 分场ID
 * @param getElementCache 可选的 BreakdownDataCacheContext 的 getElementCache 方法
 */
export const getAllElementsInBreakdownCache = async (
  breakdownID: string,
  getElementCache?: (breakdownId: string) => { elements?: ElementWithUserNames[] } | null
): Promise<ElementWithUserNames[]> => {
  // 优先从 BreakdownDataCacheContext 获取
  if (getElementCache) {
    const cache = getElementCache(breakdownID);
    if (cache && cache.elements && cache.elements.length > 0) {
      return cache.elements;
    }
  }

  // 如果没有 BreakdownDataCacheContext，尝试使用本地缓存
  const localCache = elementsCache.get(breakdownID);
  const now = Date.now();

  // 如果本地缓存存在且未过期，直接返回
  if (localCache && (now - localCache.timestamp) < CACHE_EXPIRY_TIME) {
    return localCache.items;
  }

  // 缓存不存在或已过期，从服务器拉取
  try {
    const response = await getElementsByBreakdownId(breakdownID);
    const allElements = response.data || response;

    const elementsWithUserNames: ElementWithUserNames[] = allElements.map((element: ElementInfo) => ({ ...element }));

    // 更新本地缓存（作为备用）
    elementsCache.set(breakdownID, {
      items: elementsWithUserNames,
      timestamp: now,
    });

    return elementsWithUserNames;
  } catch (error) {
    console.error('❌ 获取分场元素数据失败:', error);
    // 如果拉取失败，尝试使用过期缓存
    if (localCache) {
      console.warn('⚠️ 使用过期缓存数据');
      return localCache.items;
    }
    throw error;
  }
};

/**
 * 更新缓存中的元素（乐观更新）
 */
export const updateBreakdownItemsCache = (breakdownID: string, updater: (items: ElementWithUserNames[]) => ElementWithUserNames[]) => {
  const cache = elementsCache.get(breakdownID);
  if (cache) {
    cache.items = updater(cache.items);
    // 更新缓存时间戳
    cache.timestamp = Date.now();
  }
};

/**
 * 清除指定分场的缓存（用于强制刷新）
 */
export const clearBreakdownItemsCache = (breakdownID: string) => {
  elementsCache.delete(breakdownID);
};
